import { useEffect, useMemo, useRef, useState } from 'react'

const GAP = 12

interface Props {
    totalItems: number
    isEditable: boolean
    editorWidth?: number
    isExcludedElement?: (item: HTMLElement) => boolean
}

export const useCalculateAmountOfFitRoutes = ({
    totalItems,
    isEditable,
    editorWidth = 90,
    isExcludedElement,
}: Props) => {
    const countWidth = isEditable ? editorWidth : 60
    const containerRef = useRef<HTMLDivElement | null>(null)
    const [itemWidth, setItemWidth] = useState(0)
    const [containerWidth, setContainerWidth] = useState(0)
    let observer: ResizeObserver

    const fitCount = useMemo(() => {
        if (!containerWidth || !itemWidth) {
            return totalItems
        }

        return Math.floor((containerWidth - countWidth) / itemWidth)
    }, [itemWidth, containerWidth, countWidth])

    useEffect(() => {
        const container = containerRef.current

        if (!container || observer || !itemWidth) {
            return
        }

        observer = new ResizeObserver(() => {
            const containerWidth = container.getBoundingClientRect().width
            setContainerWidth(containerWidth)
        })

        observer.observe(container)

        return () => {
            observer.unobserve(container)
            observer.disconnect()
        }
    }, [itemWidth])

    useEffect(() => {
        const container = containerRef.current

        if (!container || !totalItems) {
            return
        }

        const measure = () =>
            requestAnimationFrame(() => {
                const children = Array.from(container.children)

                if (!children.length) {
                    return
                }

                const childrenWidth = children
                    .map((el) => (isExcludedElement?.(el as HTMLElement) ? 0 : el.getBoundingClientRect().width + GAP))
                    .filter((width) => width)

                if (!childrenWidth.length) {
                    return
                }

                const width = childrenWidth.reduce((sum, currentValue) => sum + currentValue, 0) / childrenWidth.length

                setItemWidth(Math.floor(width))
            })

        const observer = new ResizeObserver(measure)
        observer.observe(container)

        measure()

        return () => {
            observer.disconnect()
        }
    }, [totalItems])

    return { containerRef, fitCount }
}
